import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../context/SettingsContext';
import client from '../api/client';
import {
    Package, Plus, Edit2, Trash2, Check, X, Tag, List,
    Layers, Settings, ChevronRight, FileText, Globe, EyeOff, Info, Layout, 
    ChevronDown, ChevronUp, FolderTree, PackagePlus, Zap, Search
} from 'lucide-react';
import { useDevice } from '../hooks/useDevice';
import toast from 'react-hot-toast';

const ProductCard = ({ item, onEdit, onDelete, currency }) => (
    <div 
        onClick={() => onEdit(item)}
        className="premium-card p-6 md:p-10 flex items-center justify-between group active:scale-[0.98] md:active:scale-100 transition-all cursor-pointer italic hover:border-blue-500/20 shadow-2xl relative overflow-hidden"
    >
        <div className="flex items-center gap-6 relative z-10">
            <div className={`w-14 h-14 md:w-20 md:h-20 rounded-2xl md:rounded-[2rem] flex items-center justify-center border transition-all ${item.parent_id ? 'bg-neutral-950 text-amber-500 border-neutral-800' : 'bg-blue-600/10 text-blue-500 border-blue-500/20 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600'}`}>
                {item.parent_id ? <Zap size={24}/> : <Tag size={isMobile ? 24 : 32}/>}
            </div>
            <div>
                <h3 className="text-lg md:text-2xl font-black text-white uppercase italic tracking-tighter leading-tight mb-2">{item.name}</h3>
                <div className="flex items-center gap-3">
                    <p className="text-[10px] md:text-sm font-black text-blue-500 italic">{item.price?.toLocaleString()} {currency}</p>
                    <span className="text-[8px] md:text-[10px] text-neutral-600 font-black uppercase tracking-widest bg-neutral-950 px-2 py-0.5 rounded-lg border border-neutral-800">/ {item.unit}</span>
                </div>
            </div>
        </div>
        
        <div className="flex items-center gap-3 relative z-10">
             <div className="hidden md:flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                <button onClick={(e) => { e.stopPropagation(); onEdit(item); }} className="w-12 h-12 bg-neutral-800 text-white rounded-xl flex items-center justify-center hover:bg-white hover:text-black transition-all shadow-xl"><Edit2 size={18}/></button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} className="w-12 h-12 bg-rose-500/5 text-rose-500 rounded-xl flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all shadow-xl"><Trash2 size={18}/></button>
             </div>
             <ChevronRight size={20} className="text-neutral-700 md:hidden" />
        </div>

        <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-blue-600 opacity-[0.03] blur-3xl rounded-full" />
    </div>
);

const Products = () => {
    const queryClient = useQueryClient();
    const { isMobile } = useDevice();
    const { currency } = useSettings();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({ 
        name: '', unit: 'чел', price: 0, description: '', category: '', is_public: true, parent_id: null,
        is_referral_enabled: false, referral_percent: 0, tariffs: [],
        template_data: { description: '', location: '', ticket_bg_url: '', video_url: '', map_link: '', parking_info: '', entry_info: '' }
    });

    // Queries
    const { data: products, isLoading } = useQuery({
        queryKey: ['products'],
        queryFn: async () => (await client.get('/products')).data
    });

    const mutation = useMutation({
        mutationFn: (data) => editingItem ? client.patch(`/products/${editingItem.id}`, data) : client.post('/products', data),
        onSuccess: () => { queryClient.invalidateQueries(['products']); closeModal(); toast.success('Сохранено'); }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/products/${id}`),
        onSuccess: () => { queryClient.invalidateQueries(['products']); toast.success('Удалено'); }
    });

    const closeModal = () => {
        setIsModalOpen(false); setEditingItem(null);
        setFormData({ name: '', unit: 'чел', price: 0, description: '', category: '', is_public: true, parent_id: null, is_referral_enabled: false, referral_percent: 0, tariffs: [], template_data: { description: '', location: '', ticket_bg_url: '', video_url: '', map_link: '', parking_info: '', entry_info: '' } });
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({ 
            name: item.name, unit: item.unit, price: item.price, description: item.description || '', category: item.category || '', is_public: item.is_public ?? true, parent_id: item.parent_id || null,
            is_referral_enabled: item.is_referral_enabled || false, referral_percent: item.referral_percent || 0, tariffs: item.tariffs || [],
            template_data: item.template_data || { description: '', location: '', ticket_bg_url: '', video_url: '', map_link: '', parking_info: '', entry_info: '' }
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if(window.confirm('Вы действительно хотите удалить этот продукт?')) {
            deleteMutation.mutate(id);
        }
    };

    const filteredItems = products?.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase())) || [];

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">Клубный Каталог</h1>
                    <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">Million Connections Inventory Framework</p>
                </div>
                <button 
                    onClick={() => { closeModal(); setIsModalOpen(true); }} 
                    className="w-full md:w-auto bg-white text-black px-8 md:px-10 py-5 rounded-2xl md:rounded-[2rem] font-black uppercase text-[10px] md:text-xs tracking-widest hover:bg-neutral-200 transition-all shadow-2xl flex items-center justify-center gap-3 italic"
                >
                    <Plus size={20}/> {isMobile ? 'Создать' : 'Создать продукт'}
                </button>
            </header>

            {/* Responsive Search */}
            <div className="relative group">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600 transition-colors group-focus-within:text-blue-500" />
                <input 
                    type="text" 
                    placeholder="Поиск по клубным программам..." 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="w-full bg-neutral-900 border border-neutral-800 text-white pl-16 pr-6 py-5 md:py-6 rounded-[2rem] text-sm font-bold outline-none focus:border-blue-500 shadow-2xl transition-all" 
                />
            </div>

            {/* Responsive Grid View */}
            <div className="relative min-h-[400px]">
                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="h-32 md:h-40 bg-neutral-900 rounded-[2.5rem] border border-neutral-800 animate-pulse" />
                        ))}
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="py-20 text-center opacity-20 flex flex-col items-center gap-4">
                        <Package size={48} />
                        <p className="text-xs font-black uppercase tracking-widest">Каталог пуст</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-8">
                        {filteredItems.map(item => (
                            <ProductCard 
                                key={item.id} 
                                item={item} 
                                onEdit={handleEdit} 
                                onDelete={handleDelete} 
                                currency={currency} 
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Product Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[150] flex items-center justify-center p-4">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-[3rem] md:rounded-[4rem] p-8 md:p-12 shadow-2xl flex flex-col italic relative overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-10">
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter leading-none">
                                    {editingItem ? 'Правка Лота' : 'Новый Продукт'}
                                </h3>
                                <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest mt-2 italic md:block hidden">Inventory Update Protocol</p>
                            </div>
                            <button onClick={closeModal} className="w-12 h-12 md:w-14 md:h-14 bg-neutral-800 rounded-xl md:rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(formData); }} className="space-y-6 md:space-y-8">
                            <div>
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 italic">Название продукта</label>
                                <input 
                                    type="text" 
                                    required 
                                    value={formData.name} 
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                                    className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 md:p-6 rounded-2xl md:rounded-[1.5rem] font-bold outline-none focus:border-blue-500 transition-all md:text-lg" 
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 italic">Базовая Цена</label>
                                    <div className="relative">
                                        <input 
                                            type="number" 
                                            required 
                                            value={formData.price} 
                                            onChange={(e) => setFormData({ ...formData, price: e.target.value })} 
                                            className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 rounded-2xl md:rounded-[1.2rem] font-black italic outline-none focus:border-blue-500 transition-all" 
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-700 font-bold text-xs">{currency}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 italic">Единица</label>
                                    <select 
                                        value={formData.unit} 
                                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })} 
                                        className="w-full h-[62px] md:h-[68px] bg-neutral-950 border border-neutral-800 text-white px-5 rounded-2xl md:rounded-[1.2rem] font-bold appearance-none outline-none focus:border-blue-500 transition-all"
                                    >
                                        <option value="чел">чел</option>
                                        <option value="мес">мес</option>
                                        <option value="шт">шт</option>
                                        <option value="час">час</option>
                                    </select>
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                disabled={mutation.isPending}
                                className="w-full bg-blue-600 text-white py-6 md:py-8 rounded-[2rem] font-black uppercase text-[10px] md:text-xs tracking-widest shadow-2xl active:scale-95 transition-all mt-4 italic shadow-blue-600/30"
                            >
                                {mutation.isPending ? 'Синхронизация...' : 'Зафиксировать изменения'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;
